"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";
import { bulkOrderService, BulkOrderItem, BulkOrder } from "@/lib/services/bulkOrderService";

const PRODUCT_CATEGORIES = [
  "CPU 處理器", "GPU 顯示卡", "RAM 記憶體", "Motherboard 主機板",
  "Storage 儲存裝置", "Cooling 散熱", "PSU 電源供應器", "Case 機殼", "Peripherals 週邊設備"
];

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  submitted: "bg-blue-100 text-blue-700",
  processing: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-green-100 text-green-700",
  shipped: "bg-purple-100 text-purple-700",
  completed: "bg-green-200 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

type FormData = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  notes: string;
};

type ItemForm = {
  productName: string;
  category: string;
  quantity: string;
  unitPriceMin: string;
  unitPriceMax: string;
};

export default function BulkOrdersContent() {
  const [activeTab, setActiveTab] = useState<"submit" | "track">("submit");
  const [formData, setFormData] = useState<FormData>({
    companyName: "", contactName: "", email: "", phone: "", notes: ""
  });
  const [items, setItems] = useState<ItemForm[]>([{
    productName: "", category: "", quantity: "", unitPriceMin: "", unitPriceMax: ""
  }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedOrder, setSubmittedOrder] = useState<BulkOrder | null>(null);

  // Track tab
  const [trackEmail, setTrackEmail] = useState("");
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackedOrders, setTrackedOrders] = useState<BulkOrder[] | null>(null);
  const [trackError, setTrackError] = useState<string | null>(null);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleItemChange = (index: number, field: keyof ItemForm, value: string) => {
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems((prev) => [...prev, { productName: "", category: "", quantity: "", unitPriceMin: "", unitPriceMax: "" }]);
  };

  const removeItem = (index: number) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const orderItems: BulkOrderItem[] = items
      .filter((item) => item.productName.trim() && item.quantity)
      .map((item) => ({
        productId: item.category.toLowerCase().replace(/[^a-z]/g, "-") + "-custom",
        productName: item.productName,
        category: item.category,
        quantity: parseInt(item.quantity) || 0,
        unitPriceMin: parseFloat(item.unitPriceMin) || 0,
        unitPriceMax: parseFloat(item.unitPriceMax) || 0,
      }));

    if (orderItems.length === 0) {
      setSubmitError("Please add at least one product item.");
      setIsSubmitting(false);
      return;
    }

    const result = await bulkOrderService.create({
      ...formData,
      items: orderItems,
    });

    setIsSubmitting(false);
    if (result.success && result.order) {
      setSubmittedOrder(result.order);
    } else {
      setSubmitError(result.error || "Failed to submit order. Please try again.");
    }
  };

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    setTrackLoading(true);
    setTrackError(null);
    setTrackedOrders(null);
    const orders = await bulkOrderService.getByEmail(trackEmail);
    setTrackLoading(false);
    if (orders.length === 0) {
      setTrackError("No orders found for this email address.");
    } else {
      setTrackedOrders(orders);
    }
  };

  if (submittedOrder) {
    return (
      <section className="py-20 bg-bg-light min-h-screen">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-4xl p-10 border border-border shadow-navy-sm">
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={40} className="text-green-500" variant="solid" />
            </div>
            <h2 className="font-heading font-black text-3xl text-navy mb-2">Order Submitted! 訂單已提交</h2>
            <p className="text-text-muted text-sm mb-6">Your bulk order has been received. We will contact you within 24 hours.</p>
            <div className="bg-navy/5 rounded-2xl p-5 mb-6 text-left">
              <p className="text-xs text-text-muted uppercase tracking-wider font-bold mb-1">Order Reference 訂單編號</p>
              <p className="font-heading font-black text-2xl text-orange">{submittedOrder.orderReference}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-text-muted text-xs">Company</p>
                  <p className="font-bold text-navy">{submittedOrder.companyName}</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs">Total Units</p>
                  <p className="font-bold text-navy">{submittedOrder.totalUnits.toLocaleString()} units</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs">Est. Value</p>
                  <p className="font-bold text-navy">${submittedOrder.estimatedValueMin.toLocaleString()} – ${submittedOrder.estimatedValueMax.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs">Status</p>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${STATUS_COLORS[submittedOrder.orderStatus]}`}>
                    {submittedOrder.orderStatus.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-text-muted mb-6">Save your order reference number to track your order status.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setSubmittedOrder(null); setFormData({ companyName: "", contactName: "", email: "", phone: "", notes: "" }); setItems([{ productName: "", category: "", quantity: "", unitPriceMin: "", unitPriceMax: "" }]); }}
                className="px-6 py-3 bg-navy text-white rounded-full font-black text-sm hover:bg-navy-dark transition-all">
                Submit Another Order
              </button>
              <a
                href="https://wa.me/85259773386"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-green-500 text-white rounded-full font-black text-sm hover:bg-green-600 transition-all flex items-center gap-2">
                <Icon name="ChatBubbleLeftRightIcon" size={14} variant="solid" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-bg-light min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-black uppercase tracking-widest text-orange bg-orange/10 px-4 py-2 rounded-full mb-4">
            Bulk Orders 大量採購
          </span>
          <h1 className="font-heading font-black text-4xl md:text-5xl text-navy mb-4">
            Bulk Order Management
          </h1>
          <p className="text-text-muted max-w-xl mx-auto">
            Submit your bulk hardware requirements and track order status. 提交大量採購需求並追蹤訂單狀態。
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-2xl p-1.5 border border-border shadow-navy-sm max-w-sm mx-auto">
          <button
            onClick={() => setActiveTab("submit")}
            className={`flex-1 py-2.5 rounded-xl font-black text-sm transition-all ${
              activeTab === "submit" ? "bg-navy text-white shadow-navy-sm" : "text-text-muted hover:text-navy"
            }`}>
            Submit Order 提交訂單
          </button>
          <button
            onClick={() => setActiveTab("track")}
            className={`flex-1 py-2.5 rounded-xl font-black text-sm transition-all ${
              activeTab === "track" ? "bg-navy text-white shadow-navy-sm" : "text-text-muted hover:text-navy"
            }`}>
            Track Order 追蹤訂單
          </button>
        </div>

        {/* Submit Order Tab */}
        {activeTab === "submit" && (
          <div className="bg-white rounded-4xl p-8 md:p-10 border border-border shadow-navy-sm">
            <h2 className="font-heading font-black text-2xl text-navy mb-6">New Bulk Order 新增大量訂單</h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Contact Info */}
              <div>
                <h3 className="font-bold text-navy text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Icon name="UserIcon" size={16} className="text-orange" />
                  Contact Information 聯絡資料
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">Company Name 公司名稱 *</label>
                    <input type="text" name="companyName" required value={formData.companyName} onChange={handleFormChange}
                      placeholder="Your Company Ltd."
                      className="w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">Contact Name 聯絡人 *</label>
                    <input type="text" name="contactName" required value={formData.contactName} onChange={handleFormChange}
                      placeholder="Your full name"
                      className="w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">Email 電郵 *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleFormChange}
                      placeholder="you@company.com"
                      className="w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">Phone / WhatsApp</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange}
                      placeholder="+852 XXXX XXXX"
                      className="w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-navy text-sm uppercase tracking-wider flex items-center gap-2">
                    <Icon name="CubeIcon" size={16} className="text-orange" />
                    Order Items 訂單項目
                  </h3>
                  <button type="button" onClick={addItem}
                    className="flex items-center gap-1.5 text-xs font-black text-orange hover:text-orange-dark transition-colors">
                    <Icon name="PlusCircleIcon" size={16} />
                    Add Item 新增項目
                  </button>
                </div>

                <div className="flex flex-col gap-4">
                  {items.map((item, index) => (
                    <div key={index} className="bg-bg-light rounded-2xl p-4 border border-border relative">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-black text-navy uppercase">Item {index + 1}</span>
                        {items.length > 1 && (
                          <button type="button" onClick={() => removeItem(index)}
                            className="text-text-muted hover:text-red-500 transition-colors">
                            <Icon name="XMarkIcon" size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-text-muted mb-1">Product Name 產品名稱 *</label>
                          <input type="text" required value={item.productName}
                            onChange={(e) => handleItemChange(index, "productName", e.target.value)}
                            placeholder="e.g. Intel Core i9-14900K"
                            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-text-dark placeholder:text-text-muted" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-text-muted mb-1">Category 類別</label>
                          <select value={item.category} onChange={(e) => handleItemChange(index, "category", e.target.value)}
                            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-text-dark">
                            <option value="">Select...</option>
                            {PRODUCT_CATEGORIES.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-text-muted mb-1">Quantity 數量 *</label>
                          <input type="number" required min="1" value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            placeholder="100"
                            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-text-dark placeholder:text-text-muted" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-text-muted mb-1">Unit Price Min (USD)</label>
                          <input type="number" min="0" value={item.unitPriceMin}
                            onChange={(e) => handleItemChange(index, "unitPriceMin", e.target.value)}
                            placeholder="0"
                            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-text-dark placeholder:text-text-muted" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-text-muted mb-1">Unit Price Max (USD)</label>
                          <input type="number" min="0" value={item.unitPriceMax}
                            onChange={(e) => handleItemChange(index, "unitPriceMax", e.target.value)}
                            placeholder="0"
                            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-text-dark placeholder:text-text-muted" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-black text-navy uppercase tracking-wide mb-2">Additional Notes 備註</label>
                <textarea name="notes" rows={3} value={formData.notes} onChange={handleFormChange}
                  placeholder="Delivery requirements, special specifications, timeline..."
                  className="w-full bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted resize-none" />
              </div>

              {submitError && (
                <p className="text-sm text-red-500 font-semibold text-center">{submitError}</p>
              )}

              <button type="submit" disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-2xl font-black text-sm uppercase tracking-wider transition-all duration-200 shadow-navy-md">
                <Icon name="PaperAirplaneIcon" size={16} />
                {isSubmitting ? "Submitting... 提交中..." : "Submit Bulk Order 提交大量訂單"}
              </button>
            </form>
          </div>
        )}

        {/* Track Order Tab */}
        {activeTab === "track" && (
          <div className="bg-white rounded-4xl p-8 md:p-10 border border-border shadow-navy-sm">
            <h2 className="font-heading font-black text-2xl text-navy mb-2">Track Your Orders 追蹤訂單</h2>
            <p className="text-text-muted text-sm mb-6">Enter your email address to view all your bulk orders.</p>

            <form onSubmit={handleTrack} className="flex gap-3 mb-8">
              <input type="email" required value={trackEmail} onChange={(e) => setTrackEmail(e.target.value)}
                placeholder="your@company.com"
                className="flex-1 bg-bg-light border border-border rounded-xl px-4 py-3 text-sm text-text-dark placeholder:text-text-muted" />
              <button type="submit" disabled={trackLoading}
                className="flex items-center gap-2 bg-navy hover:bg-navy-dark disabled:opacity-60 text-white px-6 py-3 rounded-xl font-black text-sm transition-all">
                <Icon name="MagnifyingGlassIcon" size={16} />
                {trackLoading ? "Searching..." : "Search 搜尋"}
              </button>
            </form>

            {trackError && (
              <div className="text-center py-8">
                <Icon name="InboxIcon" size={40} className="text-text-muted mx-auto mb-3" />
                <p className="text-text-muted">{trackError}</p>
              </div>
            )}

            {trackedOrders && trackedOrders.length > 0 && (
              <div className="flex flex-col gap-4">
                {trackedOrders.map((order) => (
                  <div key={order.id} className="border border-border rounded-2xl p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-heading font-black text-lg text-navy">{order.orderReference}</p>
                        <p className="text-xs text-text-muted">{order.companyName} · {new Date(order.submittedAt).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-xs font-black px-3 py-1.5 rounded-full ${STATUS_COLORS[order.orderStatus]}`}>
                        {order.orderStatus.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-text-muted text-xs">Items</p>
                        <p className="font-bold text-navy">{order.items?.length || 0} products</p>
                      </div>
                      <div>
                        <p className="text-text-muted text-xs">Total Units</p>
                        <p className="font-bold text-navy">{order.totalUnits.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-text-muted text-xs">Est. Value</p>
                        <p className="font-bold text-navy">${order.estimatedValueMin.toLocaleString()}–${order.estimatedValueMax.toLocaleString()}</p>
                      </div>
                    </div>
                    {order.items?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs font-bold text-text-muted mb-2">ITEMS:</p>
                        <div className="flex flex-wrap gap-2">
                          {order.items.map((item, i) => (
                            <span key={i} className="text-xs bg-navy/5 text-navy px-2 py-1 rounded-lg font-semibold">
                              {item.productName} × {item.quantity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
